'use client'

import { useState, useCallback, useRef, useEffect } from 'react'

export interface AIMasterChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  meta?: {
    intent?:
      | 'AGENT'
      | 'PLATFORM'
      | 'PUBLICATIONS_DISCOVERY'
      | 'PUBLICATIONS_DISCOVERY_READY'
      | 'PUBLICATION_GENERATED_IN_CHAT'
      | 'PUBLICATION_GENERATING_IN_CHAT'
      | 'PUBLICATION_ENRICHING_INSTRUCTION'
      | 'PUBLICATION_REGENERATING_IN_CHAT'
      | 'PUBLICATION_GENERATION_FAILED'
      | 'PUBLICATION_REFINING_IN_CHAT'
      | 'PUBLICATION_REFINED_IN_CHAT'
      | 'PUBLICATION_MAKING_MORE_PREMIUM'
      | 'PUBLICATION_REGENERATED_READY'
      | 'PUBLICATION_MORE_PREMIUM_READY'
      | 'PUBLICATION_REFINEMENT_FAILED'
      | 'PUBLICATION_TEMPLATE_SELECTION'
      | 'PUBLICATION_TEMPLATE_SELECTED'
      | 'PUBLICATION_TEMPLATE_SWAPPED'
      | 'PUBLICATION_SLIDE_VISUAL_UPDATED'
      | 'PUBLICATION_SLIDE_IMAGE_GENERATION_PENDING'
      | 'PUBLICATION_SLIDE_IMAGE_UPLOAD_PENDING'
      | 'PUBLICATION_COLOR_UPDATED'
      | 'PUBLICATION_SLIDE_IMAGE_UPDATED'
      | 'PUBLICATION_EXPORT_PENDING'
      | 'PUBLICATION_EXPORT_STARTED'
      | 'PUBLICATION_EXPORT_READY'
      | 'PUBLICATION_EXPORT_ERROR'
      | 'PUBLICATION_SLIDE_EDIT_READY'
      | 'PUBLICATION_SLIDE_EDIT_FAILED'
      | 'PUBLICATION_CONTEXT_SAVED'
      | 'PUBLICATION_CONTEXT_SAVE_ERROR'
      | 'PUBLICATION_CONTEXT_REQUIRED'
      | 'PUBLICATION_IMAGE_REFERENCE_OPTION'
      | 'PUBLICATION_REFERENCE_UPLOAD_ERROR'
      | 'PUBLICATION_REFERENCE_UPLOADED'
      | 'PUBLICATION_REFERENCE_IMAGE'
      | 'FUNNEL_DISCOVERY'
      | 'FUNNEL_TEMPLATE_PICKER'
      | 'FUNNEL_NAVIGATION'
      | 'FUNNEL_SCRATCH'
      | 'AI_FUNNEL_DISCOVERY'
      | 'AI_FUNNEL_GOAL'
      | 'AI_FUNNEL_GENERATING'
      | 'AI_FUNNEL_GENERATED'
      | 'AI_FUNNEL_GENERATION_ERROR'
      | 'ADS_CAMPAIGN_REVIEW'
      | 'ADS_META_CONNECT_PLACEHOLDER'
      | 'ADS_CAMPAIGN_SIMULATED_LIVE'
      | 'ADS_CAMPAIGN_SIMULATED_RESULTS'
      | 'ADS_CAMPAIGN_SIMULATED_OPTIMIZATION'
      | 'ADS_CAMPAIGN_DISCOVERY'
      | 'ADS_NO_CREATIVE_CANDIDATES'
      | 'ADS_CREATIVE_PICKER'
      | 'ADS_CREATIVE_SELECTED'
      | 'ADS_CREATIVE_GENERATION_STARTED'
      | 'PUBLICATION_GENERATION_STARTED'
      | 'ADS_CREATIVE_GENERATED_READY'
      | 'ADS_START_CREATIVE_GENERATION'
      | 'OPEN_LATEST_PUBLICATION_CAROUSEL'
      | 'OPEN_LATEST_PUBLICATION_CAROUSEL_ERROR'
      | 'OPEN_EXPORTED_PUBLICATION_CAROUSEL'
      | 'OPEN_EXPORTED_PUBLICATION_CAROUSEL_ERROR'
      | 'OPEN_EDITABLE_PUBLICATION_CAROUSEL'
      | 'DOWNLOAD_EXPORTED_PUBLICATION_ERROR'
      | 'CREATE_CONTENT_DIRECTION_COMPLETE'
      | 'CREATE_CONTENT_DISCOVERY_ANSWER'
      | 'CREATE_CONTENT_PLAN_READY'
      | 'CREATE_CONTENT_PLAN_ERROR'
      | 'CREATE_CONTENT_ANALYZING'
      | 'CREATE_CONTENT_ADVANCED_PREPARING'
      | 'CREATE_CONTENT_ADVANCED_READY'
      | 'CREATE_CONTENT_ADVANCED_ERROR'
      | 'CREATE_CONTENT_EXPRESS_STARTING'
      | 'CREATE_CONTENT_EXPRESS_RESUMED'
      | 'CREATE_CONTENT_DRY_RUN_RESUMED'
      | 'RESET_ACTIVE_GUIDED_FLOW'
    worker?: string
    lightweightIntent?: string
    rawPrompt?: string
    strategy?: any
    discovery?: any
    plan?: any
    profile_update?: any
    profile_ready?: boolean
    recommended_next_worker?: string | null
    executed_actions?: any[]
    generated_funnel?: any
    navigation?: { route: string; params?: any } | null
    content_actions?: any[]
    crm_actions?: any[]
    buttons?: { label: string; value: string; emoji?: string }[]
    publication_flow?: any
    usage?: any
    credits?: number | null
    credits_spent?: number | null
    steps?: { key: string; label: string; emoji?: string }[]
    flow_id?: string
    slotMissing?: string | null
    routing?: any
    skill?: any
    referenceImageUrl?: string
    imageUrl?: string
    attachmentType?: 'image'
  }
}

type SendMessagePayload = {
  sellerId: number
  userId: number
  brandName: string
  agentType: 'master'
  mode: 'chat' | 'init_training'
  message: string
  pageContext?: any
  uiContext?: any
  platformContext?: any
  context?: any
  skillMode?: 'auto' | 'forced' | 'off'
  skillId?: string | number | null
}

type UseAIMasterChatOptions = {
  onActionsExecuted?: (actions: any[]) => void
  onApplyChanges?: (result: any) => void
  onFunnelGenerated?: (funnelData: any) => void
  onContentActions?: (actions: any[]) => void
  onNavigation?: (navigation: { route: string; params?: any }) => void
  onCRMAction?: (actions: any[]) => void
  onButtons?: (
    buttons: { label: string; value: string; emoji?: string }[]
  ) => void
  onPublicationFlow?: (publicationFlow: any) => void
}

const WELCOME_MSG: AIMasterChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    'Hola.\n\nSoy Samantha.\n\nPuedo ayudarte a crear algo nuevo, mejorar lo que ya tienes o encontrar la mejor forma de avanzar.\n\n¿Qué quieres hacer hoy?',
  timestamp: new Date(),
}

function buildWelcomeMessage(params?: {
  skillName?: string | null
  skillGreeting?: string | null
}): AIMasterChatMessage {
  const greeting = String(params?.skillGreeting || '').trim()
  const skillName = String(params?.skillName || '').trim()

  if (greeting) {
    return {
      id: 'welcome',
      role: 'assistant',
      content: greeting,
      timestamp: new Date(),
    }
  }

  if (skillName) {
    return {
      id: 'welcome',
      role: 'assistant',
      content: `Hola.\n\nSoy el skill **${skillName}**.\n\nCuéntame qué necesitas y te ayudo paso a paso.`,
      timestamp: new Date(),
    }
  }

  return WELCOME_MSG
}

function normalizeMessage(raw: any, fallbackId: string): AIMasterChatMessage {
  const meta =
    raw?.meta ||
    raw?.meta_json || {
      discovery: raw?.discovery || null,
      strategy: raw?.strategy || null,
      executed_actions: raw?.executed_actions || [],
      worker: raw?.worker || null,
    }

  return {
    id: String(raw?.id ?? fallbackId),
    role:
      raw?.role === 'assistant'
        ? 'assistant'
        : raw?.role === 'system'
        ? 'system'
        : 'user',
    content: String(raw?.content || ''),
    timestamp: raw?.timestamp
      ? new Date(raw.timestamp)
      : raw?.created_at
      ? new Date(raw.created_at)
      : new Date(),
    meta: meta
      ? {
          intent: meta.intent,
          worker: meta.worker,
          strategy: meta.strategy,
          discovery: meta.discovery,
          plan: meta.plan || meta.changes || null,
          profile_update: meta.profile_update,
          profile_ready: !!meta.profile_ready,
          recommended_next_worker: meta.recommended_next_worker || null,
          executed_actions: meta.executed_actions || [],
          generated_funnel: meta.generated_funnel || null,
          navigation: meta.navigation || null,
          content_actions: meta.content_actions || [],
          crm_actions: meta.crm_actions || [],
          buttons: meta.buttons || undefined,
          publication_flow: meta.publication_flow || undefined,
          usage: meta.usage || null,
          credits: typeof meta.credits === 'number' ? meta.credits : null,
          credits_spent:
            typeof meta.credits_spent === 'number' ? meta.credits_spent : null,
          steps: Array.isArray(meta.steps) ? meta.steps : [],
          flow_id: meta.flow_id || undefined,
          slotMissing: meta.slotMissing || meta.slot_missing || null,
          routing: meta.routing || null,
        }
      : undefined,
  }
}

function getSessionJson(key: string, fallback: any = {}) {
  try {
    const raw = window.sessionStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

function setSessionJson(key: string, value: any) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value || {}))
  } catch {}
}

function getSessionString(key: string, fallback: string | null = null) {
  try {
    return window.sessionStorage.getItem(key) || fallback
  } catch {
    return fallback
  }
}

function setSessionString(key: string, value: string | null | undefined) {
  try {
    if (!value) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {}
}

function getConversationStorageKey(params: {
  sellerId: number | null | undefined
  userId: number | null | undefined
  skillMode?: string
  skillId?: string | number | null
}) {
  const seller = Number(params.sellerId || 0)
  const user = Number(params.userId || 0)

  if (params.skillMode === 'forced' && params.skillId) {
    return `samantha:conversation:${seller}:${user}:skill:${params.skillId}`
  }

  return `samantha:conversation:${seller}:${user}:base`
}

function buildSkillSteps(params: {
  navigation?: { route?: string; params?: any } | null
  skill?: any
  platform?: string | null
}) {
  const nav = params.navigation
  const tab = String(nav?.params?.tab || '')

  if (!nav?.route || !tab) return undefined

  const tabLabels: Record<string, string> = {
    ideas: 'Generando ideas virales',
    hooks: 'Generando hooks irresistibles',
    scripts: 'Escribiendo el guion',
    images: 'Generando imágenes',
    video: 'Preparando el video',
  }

  const tabEmoji: Record<string, string> = {
    ideas: '💡',
    hooks: '🎯',
    scripts: '📝',
    images: '🖼️',
    video: '🎥',
  }

  return [
    { key: 'analyze', label: 'Analizando tu petición', emoji: '🧠' },
    { key: 'navigate', label: 'Abriendo Creator Studio', emoji: '🚀' },
    { key: 'open_tab', label: `Abriendo pestaña ${tab}`, emoji: tabEmoji[tab] || '✨' },
    {
      key: 'use_skill',
      label: `Activando ${params.skill?.name || 'skill experto'}`,
      emoji: '🧩',
    },
    {
      key: 'platform',
      label: `Adaptando a ${params.platform || 'la plataforma'}`,
      emoji: '📱',
    },
    {
      key: 'generate',
      label: tabLabels[tab] || 'Generando contenido',
      emoji: tabEmoji[tab] || '⚡',
    },
  ]
}

export function useAIMasterChat(
  sellerId: number | null,
  options: UseAIMasterChatOptions = {},
  context?: {
    userId?: number | null
    skillMode?: 'auto' | 'forced' | 'off'
    skillId?: string | number | null
    activeSkill?: any | null
  }
) {
  const [messages, setMessages] = useState<AIMasterChatMessage[]>([WELCOME_MSG])
  const [loading, setLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [masterAgent, setMasterAgent] = useState<any>(null)
  const [conversationId, setConversationId] = useState<number | null>(null)

  const activeUserId = Number(context?.userId || 0)
  const activeSkillMode = context?.skillMode || 'auto'
  const activeSkillId = context?.skillId || null

  const activeSkill = context?.activeSkill || null
  const currentWelcomeMsg = buildWelcomeMessage({
    skillName: activeSkill?.name,
    skillGreeting: activeSkill?.greeting,
  })

  const abortControllerRef = useRef<AbortController | null>(null)
  const lastRoutingRef = useRef<{ recommended_worker?: string | null }>({})
  const loadedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!sellerId || !activeUserId) return

    const key = getConversationStorageKey({
      sellerId,
      userId: activeUserId,
      skillMode: activeSkillMode,
      skillId: activeSkillId,
    })

    const storedConversationId = Number(
      window.localStorage.getItem(key) || 0
    )

    loadedRef.current = null

    if (storedConversationId) {
      setConversationId(storedConversationId)
    } else {
      setConversationId(null)
      setMessages([currentWelcomeMsg])
    }
  }, [sellerId, activeUserId, activeSkillMode, activeSkillId])

  useEffect(() => {
    if (!sellerId || !activeUserId) return

    let cancelled = false

    async function loadCurrentConversationId() {
      try {
        const agentId =
          activeSkillMode === 'forced' &&
          activeSkillId
            ? Number(activeSkillId)
            : Number(masterAgent?.id || 0)

        if (!agentId) {
          return
        }

        const qs =
          new URLSearchParams()

        qs.set(
          'sellerId',
          String(sellerId)
        )

        qs.set(
          'agentId',
          String(agentId)
        )

        const res = await fetch(`/api/ai/conversations/current?${qs.toString()}`, {
          cache: 'no-store',
        })

        const json = await res.json().catch(() => null)

        if (cancelled || !res.ok || !json?.ok) return

        if (json?.conversation?.id) {
          setConversationId(Number(json.conversation.id))
        } else {
          setConversationId(null)
          setMessages([currentWelcomeMsg])
        }
      } catch (err) {
        console.warn('[useAIMasterChat] Could not load current conversation:', err)
      }
    }

    loadedRef.current = null
    loadCurrentConversationId()

    return () => {
      cancelled = true
    }
  }, [
    sellerId,
    activeUserId,
    activeSkillMode,
    activeSkillId,
    masterAgent?.id,
  ])

  useEffect(() => {
    if (!sellerId || !conversationId) return

    const loadKey = `${sellerId}:${conversationId}`

    if (loadedRef.current === loadKey) return
    loadedRef.current = loadKey

    let cancelled = false

    async function hydrateConversation() {
      try {
        const res = await fetch(
          `/api/ai/chat/history?conversationId=${conversationId}&sellerId=${sellerId}`,
          { cache: 'no-store' }
        )

        if (!res.ok) return

        const data = await res.json()
        if (cancelled) return

        if (Array.isArray(data?.messages) && data.messages.length > 0) {
          const loaded: AIMasterChatMessage[] = [currentWelcomeMsg]

          for (const msg of data.messages) {
            const parsed = normalizeMessage(msg, `history-${msg?.id || Date.now()}`)
            loaded.push(parsed)

            if (
              parsed.role === 'assistant' &&
              parsed.meta?.recommended_next_worker
            ) {
              lastRoutingRef.current = {
                recommended_worker: parsed.meta.recommended_next_worker,
              }
            }
          }

          setMessages(loaded)
        }
      } catch (err) {
        console.warn('[useAIMasterChat] Could not load conversation:', err)
      }
    }

    hydrateConversation()

    return () => {
      cancelled = true
    }
  }, [sellerId, conversationId])

  useEffect(() => {
    if (!sellerId) return

    async function checkMasterAgent() {
      try {
        const res = await fetch(`/api/ai/agents/${sellerId}/master`, {
          cache: 'no-store',
        })

        if (!res.ok) {
          setMasterAgent(null)
          return
        }

        const agent = await res.json()
        setMasterAgent(agent)
      } catch (err) {
        console.error('[useAIMasterChat] Error fetching master agent:', err)
        setMasterAgent(null)
      }
    }

    checkMasterAgent()
  }, [sellerId])

  const sendMessage = useCallback(
    async (payload: SendMessagePayload) => {
      const cleanMessage = String(payload?.message || '').trim()
      if (!cleanMessage || loading) return null

      const userMsg: AIMasterChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: cleanMessage,
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, userMsg])
      setLoading(true)
      setIsStreaming(false)

      const allMessages = [...messages, userMsg]
      const conversationHistory = allMessages
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({ role: m.role, content: m.content }))

      try {
        abortControllerRef.current = new AbortController()

        const skillMode = payload.skillMode || payload.context?.routing?.skill_mode || 'auto'
        const skillId =
          payload.skillId ||
          payload.context?.routing?.preferred_agent_id ||
          null

        if (skillMode === 'forced' && skillId) {
          const conversationKey = getConversationStorageKey({
            sellerId: payload.sellerId || sellerId,
            userId: payload.userId,
            skillMode,
            skillId,
          })

          const cachedDiscovery = getSessionJson(`samantha:discovery:${skillId}`, {})
          const cachedPlatform =
            cachedDiscovery?.platform ||
            getSessionString(`samantha:platform:${skillId}`, 'YouTube Shorts')

          const response = await fetch('/api/ai/skill-chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              skill_id: skillId,
              message: cleanMessage,
              history: conversationHistory.slice(-12),
              platform: cachedPlatform,
              discovery: cachedDiscovery,
              conversationId,
              conversation_id: conversationId,
            }),
            signal: abortControllerRef.current.signal,
            cache: 'no-store',
          })

          const data = await response.json().catch(() => null)

          if (!response.ok) {
            throw new Error(data?.error || 'Hubo un problema usando el skill.')
          }

          const nextConversationId = Number(
            data?.conversationId || data?.conversation_id || 0
          )

          if (nextConversationId) {
            setConversationId(nextConversationId)

            try {
              window.localStorage.setItem(conversationKey, String(nextConversationId))
            } catch {}
          }

          if (data?.discovery) {
            setSessionJson(`samantha:discovery:${skillId}`, data.discovery)
          }

          if (data?.platform) {
            setSessionString(`samantha:platform:${skillId}`, data.platform)
          }

          if (data?.style) {
            setSessionString(`samantha:style:${skillId}`, data.style)
          }

          const steps = buildSkillSteps({
            navigation: data?.navigation || null,
            skill: data?.skill || null,
            platform: data?.platform || cachedPlatform,
          })

          const assistantMsg: AIMasterChatMessage = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: data?.message || '(sin respuesta)',
            timestamp: new Date(),
            meta: {
              intent: 'AGENT',
              worker: data?.worker || 'skill',
              navigation: data?.navigation || null,
              executed_actions: data?.skill
                ? [{ type: 'skill_used', skill: data.skill, ok: true }]
                : [],
              buttons: data?.slot_missing ? data?.buttons || undefined : undefined,
              steps,
              slotMissing: data?.slot_missing || null,
              discovery: data?.discovery || null,
              skill: data?.skill || null,
            },
          }

          setMessages((prev) => [...prev, assistantMsg])

          if (data?.navigation && options.onNavigation) {
            options.onNavigation(data.navigation)
          }

          if (
            Array.isArray(data?.buttons) &&
            data.buttons.length &&
            options.onButtons
          ) {
            options.onButtons(data.buttons)
          }

          return data
        }

        const response = await fetch('/api/ai/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sellerId: payload.sellerId,
            userId: payload.userId,
            brandName: payload.brandName,
            agentType: payload.agentType,
            mode: payload.mode,
            message: cleanMessage,
            pageContext: payload.pageContext,
            uiContext: payload.uiContext,
            platformContext: payload.platformContext,
            context: {
              ...(payload.context || {}),
              funnel_context: payload.context?.funnel_context || null,
              lesson_context: payload.context?.lesson_context || null,
              page_context: {
                ...(payload.pageContext || {}),
                currentLesson: payload.platformContext?.current_lesson || undefined,
              },
              ui_context: payload.uiContext || {},
              platform_context: payload.platformContext || {},
              conversation_history: conversationHistory,
              conversation_id: conversationId,
              routing: {
                ...(payload.context?.routing || {}),
                ...(lastRoutingRef.current?.recommended_worker
                  ? { recommended_worker: lastRoutingRef.current.recommended_worker }
                  : {}),
              },
            },
          }),
          signal: abortControllerRef.current.signal,
          cache: 'no-store',
        })

        const data = await response.json().catch(() => null)

        if (!response.ok) {
          if (response.status === 429) {
            throw new Error(
              data?.error || 'Límite de uso alcanzado. Intenta de nuevo más tarde.'
            )
          }

          if (response.status === 402) {
            const err: any = new Error(data?.error || 'Créditos agotados.')
            err.status = 402
            err.data = data
            err.details = data?.details || null
            throw err
          }

          if (response.status === 401) {
            throw new Error(data?.error || 'Sesión expirada.')
          }

          throw new Error(data?.error || 'Hubo un problema procesando tu solicitud.')
        }

        const nextConversationId = Number(
          data?.conversationId || data?.conversation_id || 0
        )

        if (nextConversationId) {
          setConversationId(nextConversationId)

          const conversationKey = getConversationStorageKey({
            sellerId: payload.sellerId || sellerId,
            userId: payload.userId,
            skillMode,
            skillId,
          })

          try {
            window.localStorage.setItem(conversationKey, String(nextConversationId))
          } catch {}
        }

        const nextWorker = data?.result?.recommended_next_worker || null
        const nextRouting = data?.routing || data?.result?.routing || null

        lastRoutingRef.current = nextRouting
          ? {
              ...nextRouting,
              recommended_worker:
                nextRouting.recommended_worker || nextWorker || null,
            }
          : {
              recommended_worker: nextWorker || null,
            }

        const assistantContent =
          data?.result?.message ||
          data?.message ||
          'He procesado tu solicitud.'

        const assistantMsg: AIMasterChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: assistantContent,
          timestamp: new Date(),
          meta: {
            intent: 'AGENT',
            worker: data?.worker || data?.result?.worker || null,
            strategy: data?.result?.strategy || null,
            discovery: data?.result?.discovery || null,
            plan: data?.result?.plan || null,
            profile_update: data?.result?.profile_update || null,
            profile_ready: !!data?.result?.profile_ready,
            recommended_next_worker: nextWorker,
            executed_actions: data?.executed_actions || [],
            generated_funnel: data?.result?.generated_funnel || null,
            navigation: data?.result?.navigation || null,
            content_actions: data?.result?.content_actions || [],
            crm_actions: data?.result?.crm_actions || [],
            buttons: data?.result?.buttons || undefined,
            publication_flow: data?.result?.publication_flow || undefined,
            usage: data?.usage || null,
            credits: typeof data?.credits === 'number' ? data.credits : null,
            credits_spent:
              typeof data?.credits_spent === 'number'
                ? data.credits_spent
                : null,
            steps: Array.isArray(data?.result?.steps) ? data.result.steps : [],
            flow_id: data?.result?.flow_id || undefined,
            slotMissing: data?.result?.slotMissing || data?.result?.slot_missing || null,
            routing: data?.routing || data?.result?.routing || null,
            skill: data?.result?.skill || null,
          },
        }

        setMessages((prev) => [...prev, assistantMsg])

        if (data?.executed_actions?.length && options.onActionsExecuted) {
          options.onActionsExecuted(data.executed_actions)
        }

        const generatedFunnel = data?.result?.generated_funnel
        if (generatedFunnel && options.onFunnelGenerated) {
          options.onFunnelGenerated(generatedFunnel)
        }

        const editorChanges = data?.result?.plan
        if (editorChanges && options.onApplyChanges) {
          options.onApplyChanges({ plan: editorChanges })
        } else if (data?.result?.strategy && options.onApplyChanges) {
          options.onApplyChanges(data.result)
        }

        const contentActions = data?.result?.content_actions
        if (
          Array.isArray(contentActions) &&
          contentActions.length &&
          options.onContentActions
        ) {
          options.onContentActions(contentActions)
        }

        const navigation = data?.result?.navigation
        if (navigation && options.onNavigation) {
          options.onNavigation(navigation)
        }

        const crmActions = data?.result?.crm_actions
        if (
          Array.isArray(crmActions) &&
          crmActions.length &&
          options.onCRMAction
        ) {
          options.onCRMAction(crmActions)
        }

        const buttons = data?.result?.buttons
        if (Array.isArray(buttons) && buttons.length && options.onButtons) {
          options.onButtons(buttons)
        }

        const publicationFlow = data?.result?.publication_flow
        if (publicationFlow && options.onPublicationFlow) {
          options.onPublicationFlow(publicationFlow)
        }

        return data
      } catch (error: any) {
        if (error?.name === 'AbortError') return null

        console.error('[useAIMasterChat] error:', error)

        const noCredits =
          error?.status === 402 ||
          error?.details?.action === 'NO_CREDITS' ||
          error?.data?.details?.action === 'NO_CREDITS'

        if (noCredits) {
          throw error
        }

        let message = error?.message || 'Error desconocido'

        if (
          error?.status === 429 ||
          String(error?.message || '').includes('quota') ||
          String(error?.message || '').includes('429')
        ) {
          message =
            'Samantha alcanzó temporalmente el límite de uso de IA. Intenta de nuevo en unos minutos.'
        }

        const errorMsg: AIMasterChatMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `Lo siento, ocurrió un problema: ${message}`,
          timestamp: new Date(),
        }

        setMessages((prev) => [...prev, errorMsg])
        return null
      } finally {
        setLoading(false)
        setIsStreaming(false)
        abortControllerRef.current = null
      }
    },
    [messages, loading, options, conversationId]
  )

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort()
    setLoading(false)
    setIsStreaming(false)
  }, [])

  const clearMessages = useCallback(() => {
    lastRoutingRef.current = {}

    try {
      window.sessionStorage.removeItem('samantha:discovery')
      window.sessionStorage.removeItem('samantha:platform')
      window.sessionStorage.removeItem('samantha:style')
    } catch {}

      if (sellerId && activeUserId) {
      const key = getConversationStorageKey({
        sellerId,
        userId: activeUserId,
        skillMode: activeSkillMode,
        skillId: activeSkillId,
      })

      try {
        window.localStorage.removeItem(key)
      } catch {}
    }

    setConversationId(null)
    loadedRef.current = null
    setMessages([currentWelcomeMsg])
  }, [sellerId, activeUserId, activeSkillMode, activeSkillId])

  const injectAssistantMessage = useCallback(
    (msg: { content: string; meta?: AIMasterChatMessage['meta'] }) => {
      const fullMsg: AIMasterChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: msg.content,
        timestamp: new Date(),
        meta: msg.meta,
      }

      setMessages((prev) => [...prev, fullMsg])
    },
    []
  )

  return {
    messages,
    setMessages,
    sendMessage,
    loading,
    isLoading: loading,
    isStreaming,
    cancelStream,
    clearMessages,
    masterAgent,
    conversationId,
    setConversationId,
    injectAssistantMessage,
  }
}